import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Star, User, Building2, Briefcase, TrendingUp, Loader2, ArrowLeft } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useUser } from '@/contexts/UserContext';
import { getAccountTypeRoute } from '@/utils/navigationUtils';
import { useSupabaseAuth } from '@/hooks/useSupabaseAuth';

type RoleOption = {
  id: 'talent' | 'agent' | 'brand' | 'venture';
  title: string;
  subtitle: string;
  description: string;
  icon: React.ComponentType<{ className?: string }>;
  gradient: string;
};

const roleOptions: RoleOption[] = [
  {
    id: 'talent',
    title: 'Athlete / Creator',
    subtitle: 'Manage your brand',
    description: 'Track performance, manage deals, and grow your personal brand with data-driven insights.',
    icon: User,
    gradient: 'from-primary to-primary-glow'
  },
  {
    id: 'agent',
    title: 'Agency / Manager',
    subtitle: 'Represent talent',
    description: 'Manage athlete portfolios, negotiate deals, and maximize earning potential for your clients.',
    icon: Briefcase,
    gradient: 'from-chart-2 to-blue-400'
  },
  {
    id: 'brand',
    title: 'Business / Brand',
    subtitle: 'Find partnerships',
    description: 'Discover athletes, create campaigns, and measure the impact of your sponsorship investments.',
    icon: Building2,
    gradient: 'from-chart-3 to-orange-400'
  },
  {
    id: 'venture',
    title: 'Venture Firm',
    subtitle: 'Invest in talent',
    description: 'Evaluate athlete investment opportunities and manage portfolio performance with advanced analytics.',
    icon: TrendingUp,
    gradient: 'from-chart-4 to-purple-400'
  }
];

// Check for auth tokens in URL hash
const hasAuthTokensInHash = () => {
  const hash = window.location.hash;
  if (!hash) return false;
  const params = new URLSearchParams(hash.substring(1));
  return params.has('access_token') || params.get('type') === 'signup';
};

const RoleSelectionPage = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useSupabaseAuth();
  const { setAccountType, accountType, betaAccess, loading: userLoading } = useUser();
  const [loading, setLoading] = useState(false);
  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const [waitingForAuth, setWaitingForAuth] = useState(() => {
    // Start waiting if we have signup verification flag or tokens in hash
    return sessionStorage.getItem('isSignupVerification') === 'true' || hasAuthTokensInHash();
  });

  // Handle authentication - wait for tokens to be processed
  useEffect(() => {
    const isSignupFlow = sessionStorage.getItem('isSignupVerification') === 'true' || hasAuthTokensInHash();
    
    if (!authLoading) {
      if (user) {
        // User is authenticated, proceed
        console.log('RoleSelectionPage: User authenticated, proceeding');
        setWaitingForAuth(false);
        // Clear the flag now that we're authenticated
        sessionStorage.removeItem('isSignupVerification');
      } else if (!isSignupFlow) {
        // No user and not a signup flow, redirect to auth
        console.log('RoleSelectionPage: No user and not signup flow, redirecting to auth');
        navigate('/auth', { replace: true });
      }
      // If it's a signup flow but no user yet, keep waiting (Supabase is still processing)
    }
  }, [user, authLoading, navigate]);

  // Timeout for signup verification - if auth doesn't complete in 8 seconds, redirect to auth
  useEffect(() => {
    if (waitingForAuth && !user) {
      const timeout = setTimeout(() => {
        if (!user) {
          console.log('RoleSelectionPage: Timeout waiting for auth, redirecting to auth');
          sessionStorage.removeItem('isSignupVerification');
          navigate('/auth', { replace: true });
        }
      }, 8000);
      return () => clearTimeout(timeout);
    }
  }, [waitingForAuth, user, navigate]);

  // Redirect if user already has an account type and setup is completed
  useEffect(() => {
    if (user && accountType && betaAccess && !userLoading) {
      console.log('RoleSelectionPage: User already has account type', accountType, '- redirecting to onboarding');
      navigate(`/onboarding?type=${accountType}`, { replace: true });
    }
  }, [user, accountType, betaAccess, userLoading, navigate]);

  // Show loading while waiting for auth during signup verification
  if (waitingForAuth && !user) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="text-muted-foreground">Verifying your account...</p>
        </div>
      </div>
    );
  }

  // Show loading while user context is loading
  if (authLoading || userLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center space-y-4">
          <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  const handleRoleSelect = async (roleId: 'talent' | 'agent' | 'brand' | 'venture') => {
    setLoading(true);
    setSelectedRole(roleId);

    try {
      // Get current user
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('Please sign in first');
        navigate('/auth');
        return;
      }

      // Only update account_type - do NOT set setup_completed yet
      // The onboarding flow will handle creating account records and setting setup_completed
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ 
          account_type: roleId
          // setup_completed stays false until onboarding is complete
        })
        .eq('user_id', user.id);

      if (updateError) throw updateError;

      // Update context
      setAccountType(roleId);
      
      toast.success(`Let's set up your ${roleOptions.find(r => r.id === roleId)?.title} account!`);
      
      // Redirect to onboarding wizard
      navigate(`/onboarding?type=${roleId}`);
    } catch (error) {
      console.error('Role selection error:', error);
      toast.error('Failed to proceed. Please try again.');
    } finally {
      setLoading(false);
      setSelectedRole(null);
    }
  };

  const handleReturnToLogin = async () => {
    try {
      // Sign out the user and return to auth page
      await supabase.auth.signOut();
      toast.info('Returned to login');
      navigate('/auth');
    } catch (error) {
      console.error('Error signing out:', error);
      navigate('/auth');
    }
  };

  return (
    <div className="min-h-screen bg-background p-4">
      <div className="container mx-auto max-w-4xl space-y-8">
        {/* Header */}
        <div className="text-center space-y-4 pt-8 relative">
          {/* Return to Login Button */}
          <div className="absolute top-0 right-0">
            <Button
              variant="outline"
              onClick={handleReturnToLogin}
              className="flex items-center space-x-2"
              disabled={loading}
            >
              <ArrowLeft className="h-4 w-4" />
              <span>Return to Login</span>
            </Button>
          </div>
          
          <div className="flex items-center justify-center space-x-2 mb-4">
            <Star className="h-8 w-8 text-primary" />
            <span className="text-2xl font-bold text-primary">TheSlice</span>
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground">
            Choose Your Role
          </h1>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Select the option that best describes you to customize your experience
          </p>
        </div>

        {/* Role Selection Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto">
          {roleOptions.map((role) => {
            const Icon = role.icon;
            const isLoading = loading && selectedRole === role.id;
            
            return (
              <Card 
                key={role.id} 
                className="robinhood-card cursor-pointer transition-all duration-300 hover:shadow-robinhood-lg hover:scale-[1.02] group"
                onClick={() => !loading && handleRoleSelect(role.id as any)}
              >
                <CardHeader className="text-center space-y-4">
                  <div className={`w-16 h-16 mx-auto rounded-full bg-gradient-to-br ${role.gradient} flex items-center justify-center shadow-lg group-hover:shadow-xl transition-shadow duration-300`}>
                    <Icon className="h-8 w-8 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-xl sm:text-2xl font-bold text-foreground">
                      {role.title}
                    </CardTitle>
                    <CardDescription className="text-sm font-medium text-primary">
                      {role.subtitle}
                    </CardDescription>
                  </div>
                </CardHeader>
                <CardContent className="text-center space-y-4">
                  <p className="text-muted-foreground text-sm leading-relaxed">
                    {role.description}
                  </p>
                  <Button 
                    className={`w-full robinhood-button bg-gradient-to-r ${role.gradient} hover:opacity-90 transition-opacity`}
                    disabled={loading}
                  >
                    {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    {isLoading ? 'Setting up...' : `Get Started as ${role.id === 'talent' ? 'Athlete/Creator' : role.title.split(' ')[0]}`}
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Footer */}
        <div className="text-center text-sm text-muted-foreground">
          <p>You can change your role anytime in your profile settings</p>
        </div>
      </div>
    </div>
  );
};

export default RoleSelectionPage;